import React, { useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LayoutDashboard, Users, Briefcase, ListChecks, LogOut, ShieldCheck, MessageSquarePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LakshLogo } from '@/components/LakshLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { icon: LayoutDashboard, label: 'Overview', path: '/admin/dashboard' },
  { icon: Users, label: 'Users', path: '/admin/users' },
  { icon: Briefcase, label: 'CA Firms', path: '/admin/cas' },
  { icon: ListChecks, label: 'Waitlist', path: '/admin/waitlist' },
  { icon: MessageSquarePlus, label: 'Feedback', path: '/admin/feedback' },
];

export const clearAdminSession = () => {
  localStorage.removeItem('laksh_admin_token');
  localStorage.removeItem('laksh_admin_user');
};

export const useAdminApi = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('laksh_admin_token');
  return useMemo(() => {
    if (!token) return null;
    const client = axios.create({
      baseURL: `${BACKEND_URL}/api/admin`,
      headers: { Authorization: `Bearer ${token}` },
    });
    client.interceptors.response.use(
      (r) => r,
      (err) => {
        if ([401, 403].includes(err.response?.status)) {
          clearAdminSession();
          navigate('/admin/login');
        }
        return Promise.reject(err);
      }
    );
    return client;
  }, [token, navigate]);
};

export const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('laksh_admin_token');
  const user = JSON.parse(localStorage.getItem('laksh_admin_user') || '{}');

  useEffect(() => {
    if (!token) navigate('/admin/login');
  }, [token, navigate]);

  const handleLogout = () => {
    clearAdminSession();
    toast.success('Logged out');
    navigate('/admin/login');
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-[#06100A] text-white">
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0a1a12] border-r border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <LakshLogo height={32} />
          <div className="mt-2 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#EF9F27]" />
            <span className="text-xs text-[#EF9F27] font-medium">Admin Console</span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors hover:bg-white/5 hover:text-white ${
                location.pathname === item.path ? 'bg-[#EF9F27]/15 text-[#EF9F27]' : 'text-gray-400'
              }`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <p className="text-sm font-medium truncate">{user.name}</p>
          <p className="text-xs text-gray-500 truncate mb-3">{user.email}</p>
          <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout-btn" className="w-full text-gray-400 hover:text-white hover:bg-white/5">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>
      <main className="ml-64 p-8">
        <div className="max-w-6xl mx-auto space-y-8">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
